// ══════════════════════════════════════════════════════════════════
//  Prefetch estratégico (TanStack Query)
//  - prefetchColdQueries: aquece as queries "frias" compartilhadas quando
//    o shell autenticado monta → 1ª navegação (Kanban/Leads/Settings/…) já
//    vem quente.
//  - prefetchRoute: aquece os dados-chave de uma rota no hover do item de
//    nav → abrir a rota não mostra loading.
//  prefetchQuery é no-op se a query já estiver fresca (dedup por staleTime),
//  então chamar de mais é barato.
// ══════════════════════════════════════════════════════════════════
import type { QueryClient } from '@tanstack/react-query';
import { qk } from './keys';
import { fetchLeadsList } from '@hooks/useLeads';
import { loadFilterOptions } from '@app';
import { listCommissions } from '@app';
import { listFunnels, listPipelineIds } from '@app';
import { getCommissionRates, getWorkspaceOrigins } from '@app';
import { listUsersByRole } from '@app';
import { getPeriodKey } from '@utils';

const STALE = 60_000; // 1 min — janela em que o prefetch é considerado fresco

interface PrefetchOpts {
  queryKey: readonly unknown[];
  queryFn: () => Promise<unknown>;
}

const fire = (qc: QueryClient, opts: PrefetchOpts): void => {
  void qc.prefetchQuery({ ...opts, staleTime: STALE });
};

// ── Primitivas reutilizáveis por chave ───────────────────────────
const pfLeads = (qc: QueryClient, ws: string): void => {
  fire(qc, { queryKey: qk.leads(ws), queryFn: () => fetchLeadsList(ws) });
};
const pfFilterOptions = (qc: QueryClient, ws: string) =>
  fire(qc, { queryKey: qk.filterOptions(ws), queryFn: () => loadFilterOptions(ws) });
const pfFunnels = (qc: QueryClient, ws: string) =>
  fire(qc, { queryKey: qk.funnels(ws), queryFn: () => listFunnels(ws) });
const pfPipelineIds = (qc: QueryClient, ws: string) =>
  fire(qc, { queryKey: qk.pipelineIds(ws, null, null), queryFn: () => listPipelineIds({ workspaceId: ws, funnelId: null, role: null }) });
const pfCommissionRates = (qc: QueryClient, ws: string) =>
  fire(qc, { queryKey: qk.config.commissionRates(ws), queryFn: () => getCommissionRates(ws) });
const pfWorkspaceOrigins = (qc: QueryClient, ws: string) =>
  fire(qc, { queryKey: qk.config.workspaceOrigins(ws), queryFn: () => getWorkspaceOrigins(ws) });
const pfUsers = (qc: QueryClient, ws: string) => {
  fire(qc, { queryKey: qk.users.byRole(ws, 'sdr'), queryFn: () => listUsersByRole(ws, 'sdr') });
  fire(qc, { queryKey: qk.users.byRole(ws, 'closer'), queryFn: () => listUsersByRole(ws, 'closer') });
};
const pfCommissions = (qc: QueryClient, ws: string) => {
  // "Todas do período" do mês corrente — a visão default da CommissionsPage.
  const period = getPeriodKey(new Date());
  fire(qc, { queryKey: qk.commissions(ws, period, null, null), queryFn: () => listCommissions({ workspaceId: ws, period }) });
};

// Aquece o conjunto universal usado por quase todas as telas.
export function prefetchColdQueries(qc: QueryClient, workspaceId: string | null | undefined): void {
  if (!workspaceId) return;
  const ws = workspaceId;
  pfLeads(qc, ws);            // Kanban / Leads / Agenda
  pfFilterOptions(qc, ws);    // filtros de Kanban / Leads
  pfFunnels(qc, ws);          // Dashboard / Kanban / Metas
  pfPipelineIds(qc, ws);      // Dashboard / Comissões
  pfCommissionRates(qc, ws);  // Comissões / Settings
  pfWorkspaceOrigins(qc, ws); // Settings / Metas
  pfUsers(qc, ws);            // dropdowns de responsável
}

// Aquece os dados-chave de uma rota específica (hover do nav).
export function prefetchRoute(qc: QueryClient, view: string, workspaceId: string | null | undefined): void {
  if (!workspaceId) return;
  const ws = workspaceId;
  switch (view) {
    case '':
    case 'dashboard':
      pfFunnels(qc, ws);
      pfFilterOptions(qc, ws);
      pfPipelineIds(qc, ws);
      break;
    case 'kanban':
    case 'leads':
    case 'agenda':
      pfLeads(qc, ws);
      pfFilterOptions(qc, ws);
      pfFunnels(qc, ws);
      break;
    case 'commissions':
      pfCommissions(qc, ws);
      pfCommissionRates(qc, ws);
      pfUsers(qc, ws);
      break;
    case 'goals':
    case 'settings':
      pfCommissionRates(qc, ws);
      pfWorkspaceOrigins(qc, ws);
      pfUsers(qc, ws);
      break;
    default:
      break;
  }
}
